import { cn } from '@bia-auction/ui';
import type { Lang } from '@bia-auction/shared-i18n';
import { t } from '@bia-auction/shared-i18n';
import { useAuth } from '../lib/auth';
import { changeLang, useLang } from '../lib/i18n';
import { persistLocale } from '../lib/locale-sync';

/** Endonym per code — the button's visible text is the 2-letter code, so the
 *  accessible name must carry the full language name (what the old `<Select>`
 *  options announced). */
const LANGS: readonly { code: Lang; name: string }[] = [
  { code: 'tr', name: 'Türkçe' },
  { code: 'en', name: 'English' },
];

/**
 * tr/en language switcher (M7) — the brand site's paired TR/EN pills rather than a
 * dropdown: two options don't justify a menu, and the pills read at a glance in the
 * header. Persists via `changeLang`; re-renders the tree through `useLang`.
 */
export function LanguageSwitcher() {
  const lang = useLang();
  const { state } = useAuth();

  const onChange = (next: Lang) => {
    if (next === lang) return;
    changeLang(next);
    // Keep the server-side notification locale in sync so native push matches the chosen UI
    // language (M8). Retried with backoff — a transient failure would otherwise leave the server
    // locale diverged from the UI (stale-language push). Anonymous visitors skip it (set at register).
    if (state.authenticated) void persistLocale(next);
  };

  return (
    <div
      role="group"
      aria-label={t('bidder.lang.label')}
      className="flex items-center gap-1 text-xs font-medium"
    >
      {LANGS.map(({ code, name }) => (
        <button
          key={code}
          type="button"
          // The visible text is the code, so the accessible name spells the language out.
          // aria-pressed carries the selected state for assistive tech; the sighted cue is
          // fill + weight, never color alone (docs/15 §1.2, §6).
          aria-label={name}
          aria-pressed={code === lang}
          onClick={() => onChange(code)}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full border uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            code === lang
              ? 'border-transparent bg-primary font-semibold text-primary-foreground'
              : 'border-border-strong text-muted-foreground hover:text-foreground',
          )}
        >
          {code}
        </button>
      ))}
    </div>
  );
}
